import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import React from 'react';

import type { CustomLinksGroup } from 'types/footerLinks';

import config from 'configs/app';
import type { ResourceError } from 'lib/api/resources';
import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
import useIssueUrl from 'lib/hooks/useIssueUrl';
import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { copy } from 'toolkit/utils/htmlEntities';
import IconSvg from 'ui/shared/IconSvg';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import NetworkLogo from '../networkLogo/NetworkLogo';
import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';
import getApiVersionUrl from './utils/getApiVersionUrl';
const logoIcon = (
  <svg width="518" height="148" viewBox="0 0 518 148" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M142.285 10C138.897 25.978 136.824 44.0145 137.324 43.7425C145.024 39.5495 150.637 37.6475 156.553 37.2235C168.798 36.3475 178.294 40.4705 183.161 48.7765C187.541 56.2505 187.413 63.7685 182.482 88.4995C180.179 100.05 178.055 110.512 177.762 111.75C177.243 113.941 176.886 114 164.221 114C151.269 114 151.215 113.99 151.647 111.75C151.886 110.512 153.828 100.654 155.963 89.8425C160.57 66.5095 160.389 63.6925 154.087 60.6145C146.877 57.0945 137.03 61.1025 132.8 69.2815C131.884 71.0515 129.263 81.7245 126.976 92.9995L122.817 113.5L109.516 113.78L96.214 114.06L96.756 111.78C97.054 110.526 112.208 36.675 117.5 10H142.285ZM251.014 38.4675C253.727 39.2755 257.436 41.3005 259.258 42.9675C261.079 44.6355 262.934 45.9995 263.38 45.9995C263.826 45.9995 264.478 44.1995 264.83 41.9995L265.47 37.9995H278.458C285.601 37.9995 291.446 38.3555 291.446 38.7895C291.446 39.6035 277.908 107.723 276.945 111.75C276.424 113.934 276.042 114 263.997 114H251.586L252.446 111C253.454 107.485 253.148 107.365 249.6 109.886C241.288 115.79 227.151 117.331 218.27 113.3C199.841 104.936 193.342 81.7775 203.395 60.2955C211.691 42.5665 232.597 32.9835 251.014 38.4675ZM498.895 39.9895C505.225 42.4395 513.551 49.3675 516.107 54.3105C516.925 55.8915 517.42 57.3585 517.208 57.5705C516.996 57.7825 512.133 60.1495 506.402 62.8305L495.981 67.7055L491.859 63.4635C479.905 51.1608 461.535 62.7361 461.468 78.7995C461.427 88.6355 466.593 93.9885 476.131 93.9955C482.857 94.0005 486.025 92.7255 490.147 88.3555L493.509 84.7905L503.002 90.4555L512.496 96.1215L510.562 98.8105C501.49 111.429 483.73 117.883 465.946 115.024C447.089 111.992 435.764 100.63 434.674 83.6525C433.305 62.3145 446.344 44.4525 467.446 38.7595C476.55 36.3025 490.815 36.8605 498.895 39.9895ZM49.91 47.5145C52.64 52.7315 55.115 57.0145 55.41 57.0325C55.705 57.0505 59.996 52.7555 64.946 47.4885L73.946 37.9105L88.178 38.2055L102.41 38.4995L84.428 57.1625C74.538 67.4275 66.446 76.2425 66.446 76.7515C66.446 77.2615 70.913 85.8625 76.373 95.8655L86.3 114.054L72.159 113.777L58.018 113.5L53.291 104.52C50.692 99.5805 48.201 95.4245 47.755 95.2845C47.31 95.1435 43.121 99.2745 38.446 104.465L29.946 113.899L14.973 113.95L0 114L6.966 106.75C10.798 102.762 19.25 94.0665 25.749 87.4255L37.565 75.3515L29.127 59.8605C24.486 51.3415 19.948 42.9365 19.042 41.1845L17.395 37.9995L31.17 38.0145L44.946 38.0285L49.91 47.5145ZM325.94 39.2495C326.233 40.4865 327.802 49.1355 329.427 57.2455L332.381 71.9925L323.245 86.4955C318.219 94.4725 313.836 101 313.503 101C313.17 101 310.026 87.3875 306.517 70.7495C303.008 54.1115 299.933 38.9375 299.685 38.2495C299.685 38.2495 323.005 38.7647 325.94 39.2495ZM427.863 45.0885C427.12 48.9875 423.731 66.0875 420.332 83.0885L414.152 114H401.299C391.684 114 388.445 113.685 388.444 112.75C388.442 111.369 402.294 41.2465 402.964 39.2495C403.271 38.3355 406.856 37.9995 416.298 37.9995H429.214L427.863 45.0885ZM235.618 60.1435C233.009 61.4345 230.235 63.9165 228.633 66.3925C218.512 82.0282 235.33 102.478 250.633 90.8055C257.464 85.5955 260.529 73.1905 256.882 65.5065C253.588 58.5655 243.796 56.0955 235.618 60.1435Z" fill="white"/>
    <ellipse cx="419.445" cy="14.5" rx="17" ry="14.5" fill="white"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M393.115 39.25C392.551 39.938 385.062 49.05 376.472 59.5L360.854 78.5L374.569 79L388.284 79.5L351.623 113.75C331.46 132.588 314.735 148 314.458 148C314.181 148 318.474 140.974 323.999 132.388C329.524 123.801 334.284 116.151 334.576 115.388C335.005 114.27 333.432 114 326.502 114C321.768 114 317.895 113.795 317.895 113.545C317.895 113.294 323.183 105.019 329.645 95.155C336.107 85.292 346.71 68.397 353.207 57.611L365.019 38H379.58C391.108 38 393.926 38.26 393.115 39.25Z" fill="#FFA32C"/>
    <ellipse cx="420" cy="14.5" rx="17" ry="14.5" fill="white"/>
  </svg>
);

const MAX_LINKS_COLUMNS = 4;

const FRONT_VERSION_URL = `https://github.com/blockscout/frontend/tree/${ config.UI.footer.frontendVersion }`;
const FRONT_COMMIT_URL = `https://github.com/blockscout/frontend/commit/${ config.UI.footer.frontendCommit }`;

const Footer = () => {

  const { data: backendVersionData } = useApiQuery('general:config_backend_version', {
    queryOptions: {
      staleTime: Infinity,
      enabled: !config.features.opSuperchain.isEnabled,
    },
  });
  const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  const issueUrl = useIssueUrl(backendVersionData?.backend_version);

  const BLOCKSCOUT_LINKS = [
    {
      icon: 'edit' as const,
      iconSize: '16px',
      text: 'Submit an issue',
      url: issueUrl,
    },
    {
      icon: 'social/git' as const,
      iconSize: '18px',
      text: 'Contribute',
      url: 'https://github.com/blockscout/blockscout',
    },
  ];

  // All Xhavic social accounts are wired to their real URLs.
  const SOCIAL_LINKS = [
    { icon: 'social/twitter_filled' as const, iconSize: '18px', text: 'Twitter / X', url: 'https://x.com/XhavicLabs', isDisabled: false },
    { icon: 'social/youtube_filled' as const, iconSize: '18px', text: 'YouTube', url: 'https://youtube.com/@xhavichain', isDisabled: false },
    { icon: 'social/instagram_filled' as const, iconSize: '18px', text: 'Instagram', url: 'https://www.instagram.com/xhavicofficial/', isDisabled: false },
    { icon: 'social/facebook_filled' as const, iconSize: '18px', text: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61587340254751', isDisabled: false },
    { icon: 'social/linkedin_filled' as const, iconSize: '18px', text: 'LinkedIn', url: 'https://www.linkedin.com/company/xhavic/', isDisabled: false },
    { icon: 'social/discord_filled' as const, iconSize: '18px', text: 'Discord', url: 'https://xhavic.com/', isDisabled: false },
    { icon: 'social/telegram_filled' as const, iconSize: '18px', text: 'Telegram', url: 'https://t.me/Xhaviccommunity', isDisabled: false },
    { icon: 'social/github_filled' as const, iconSize: '18px', text: 'GitHub', url: 'https://xhavic.com/', isDisabled: false },
  ];

  // "Explorer" and "API" already have real pages in this app; the rest are placeholders
  // (no bridge/RPC page or external docs/repo URL configured yet)
  const RESOURCE_LINKS = [
    { text: 'Website', url: 'https://xhavic.com' },
    { text: 'Explorer', url: '/' },
    { text: 'API', url: '/api-docs' },
    { text: 'Documentation', url: '#' },
    { text: 'GitHub', url: 'https://xhavic.com/' },
    { text: 'RPC', url: 'https://rpc.xhaviscan.com/' },
    { text: 'Bridge', url: '#' },
  ];

  // TODO: placeholder urls -- link to real Terms / Privacy / Contact pages once they exist
  const LEGAL_LINKS = [
    { text: 'Terms', url: '#' },
    { text: 'Privacy', url: '#' },
    { text: 'Contact', url: '#' },
  ];

  const frontendLink = (() => {
    if (config.UI.footer.frontendVersion) {
      return <Link href={ FRONT_VERSION_URL } external noIcon>{ config.UI.footer.frontendVersion }</Link>;
    }

    if (config.UI.footer.frontendCommit) {
      return <Link href={ FRONT_COMMIT_URL } external noIcon>{ config.UI.footer.frontendCommit }</Link>;
    }

    return null;
  })();

  const fetch = useFetch();

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: [ 'footer-links' ],
    queryFn: async() => fetch(config.UI.footer.links || '', undefined, { resource: 'footer-links' }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;

  const renderNetworkInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Flex
        alignItems="center"
        gridArea={ gridArea }
        flexWrap="wrap"
        justifyContent="flex-start"
        columnGap={ 3 }
        rowGap={ 2 }
        mb={{ base: 5, lg: 10 }}
        _empty={{ display: 'none' }}
      >
        { /* {!config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus />} */ }
        <NetworkAddToWallet source="Footer"/>
      </Flex>
    );
  }, []);

  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    const logoColor = { base: 'blue.600', _dark: 'white' };

    return (
      <Box gridArea={ gridArea }>
        <Flex columnGap={ 2 } textStyle="xs" alignItems="center">
          { /* <span>Made with</span> */ }
          <Link href="https://xhaviscan.com" external noIcon display="inline-flex" color={ logoColor } _hover={{ color: logoColor }}>
            { /* <IconSvg
              name="networks/logo-placeholder"
              width="80px"
              height={ 4 }
            /> */ }
            { /* <Image src={logoIcon} height={4} width={80} alt={'logo'} /> */ }
            <NetworkLogo/>
          </Link>
        </Flex>
        <Text mt={ 3 } fontSize="xs">
          Xhavic is the official block explorer for the Xhavic Network — an OP Stack Layer-2 blockchain. Search and analyze blocks, transactions, addresses, and tokens in real time.
        </Text>
        <Box mt={ 6 } alignItems="start" textStyle="xs">
          { /* {apiVersionUrl && (
            <Text>
              Backend: <Link href={apiVersionUrl} external noIcon>{backendVersionData?.backend_version}</Link>
            </Text>
          )}
          {frontendLink && (
            <Text>
              Frontend: {frontendLink}
            </Text>
          )} */ }
          <Text>
            Copyright { copy } Xhavic Limited { (new Date()).getFullYear() }
          </Text>
        </Box>
      </Box>
    );
  }, [ apiVersionUrl, backendVersionData?.backend_version, frontendLink ]);

  const containerProps: HTMLChakraProps<'div'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'border.divider',
  };

  const contentProps: GridProps = {
    px: { base: 4, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 12, '2xl': 6 },
    py: { base: 4, lg: 8 },
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
    maxW: `${ CONTENT_MAX_WIDTH }px`,
    m: '0 auto',
  };

  const renderRecaptcha = (gridArea?: GridProps['gridArea']) => {
    if (!config.services.reCaptchaV2.siteKey) {
      return <Box gridArea={ gridArea }/>;
    }

    return (
      <Box gridArea={ gridArea } textStyle="xs" mt={ 6 }>
        <span>This site is protected by reCAPTCHA and the Google </span>
        <Link href="https://policies.google.com/privacy" external noIcon>Privacy Policy</Link>
        <span> and </span>
        <Link href="https://policies.google.com/terms" external noIcon>Terms of Service</Link>
        <span> apply.</span>
      </Box>
    );
  };

  if (config.UI.footer.links) {
    return (
      <Box { ...containerProps }>
        <Grid { ...contentProps }>
          <div>
            { renderNetworkInfo() }
            { renderProjectInfo() }
            { renderRecaptcha() }
          </div>

          <Grid
            gap={{ base: 6, lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8, xl: 12 }}
            gridTemplateColumns={{
              base: 'repeat(auto-fill, 160px)',
              lg: `repeat(${ colNum }, 135px)`,
              xl: `repeat(${ colNum }, 160px)`,
            }}
            justifyContent={{ lg: 'flex-end' }}
            mt={{ base: 8, lg: 0 }}
          >
            {
              ([
                { title: 'Xhavic', links: BLOCKSCOUT_LINKS },
                ...(linksData || []),
              ])
                .slice(0, colNum)
                .map(linkGroup => (
                  <Box key={ linkGroup.title }>
                    <Skeleton fontWeight={ 500 } mb={ 3 } display="inline-block" loading={ isPlaceholderData }>{ linkGroup.title }</Skeleton>
                    <VStack gap={ 1 } alignItems="start">
                      { linkGroup.links.map(link => <FooterLinkItem { ...link } key={ link.text } isLoading={ isPlaceholderData }/>) }
                    </VStack>
                  </Box>
                ))
            }
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box { ...containerProps }>
      <Grid
        { ...contentProps }
        gridTemplateAreas={{
          lg: `
          "network links-top"
          "info links-bottom"
          "recaptcha links-bottom"
        `,
        }}
      >

        { renderNetworkInfo({ lg: 'network' }) }
        { renderProjectInfo({ lg: 'info' }) }
        { renderRecaptcha({ lg: 'recaptcha' }) }

        <Grid
          gridArea={{ lg: 'links-bottom' }}
          gap={{ base: 6, lg: 8, xl: 10 }}
          gridTemplateColumns={{
            base: 'repeat(auto-fill, 160px)',
            lg: 'repeat(4, 140px)',
            xl: 'repeat(4, 150px)',
          }}
          alignContent="start"
          justifyContent={{ lg: 'flex-end' }}
          mt={{ base: 8, lg: 0 }}
        >
          {
            [
              { title: 'Xhavic', links: BLOCKSCOUT_LINKS },
              { title: 'Resources', links: RESOURCE_LINKS },
              { title: 'Legal', links: LEGAL_LINKS },
              { title: 'Social', links: SOCIAL_LINKS },
            ].map(linkGroup => (
              <Box key={ linkGroup.title }>
                <Box fontWeight={ 500 } mb={ 3 }>{ linkGroup.title }</Box>
                <VStack gap={ 1 } alignItems="start">
                  { linkGroup.links.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
                </VStack>
              </Box>
            ))
          }
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(Footer);
